import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  Bot, Key, Activity, Server, AlertTriangle, Network
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getAgents, getAgentGraph } from '../services/api';

// Define nodeTypes outside component to prevent recreation
const nodeTypes = {};
const edgeTypes = {};

const GraphPage = () => {
  const [searchParams] = useSearchParams();
  const selectedAgentParam = searchParams.get('agent');
  
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(selectedAgentParam || '');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      fetchGraph(selectedAgent);
    }
  }, [selectedAgent]);

  const fetchAgents = async () => {
    try {
      const response = await getAgents();
      setAgents(response.data);
      if (selectedAgentParam) {
        setSelectedAgent(selectedAgentParam);
      } else if (response.data.length > 0) {
        setSelectedAgent(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGraph = async (agentId) => {
    try {
      const response = await getAgentGraph(agentId);
      const graphData = response.data;
      
      // Transform nodes for ReactFlow
      const flowNodes = graphData.nodes.map((node, index) => {
        const yPos = getNodeYPosition(node.type);
        const xPos = 150 + (index % 4) * 250;
        
        return {
          id: node.id,
          position: { x: xPos, y: yPos },
          data: { 
            label: renderNodeLabel(node),
            ...node.data 
          },
          style: getNodeStyle(node.type),
          type: 'default',
        };
      });

      // Transform edges for ReactFlow
      const flowEdges = graphData.edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        style: { stroke: '#27272A', strokeWidth: 2 },
        labelStyle: { fill: '#9CA3AF', fontSize: 10 },
        labelBgStyle: { fill: '#121212' },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#27272A',
        },
        animated: true,
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
    } catch (error) {
      console.error('Failed to fetch graph:', error);
    }
  };

  const getNodeYPosition = (type) => {
    switch (type) {
      case 'agent': return 50;
      case 'credential': return 200;
      case 'tool': return 350;
      case 'resource': return 500;
      default: return 300;
    }
  };

  const getNodeStyle = (type) => {
    const baseStyle = {
      padding: '16px',
      borderRadius: '0',
      border: '2px solid',
      minWidth: '180px',
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '12px',
    };

    switch (type) {
      case 'agent':
        return { ...baseStyle, background: '#0066FF20', borderColor: '#0066FF', color: '#F9FAFB' };
      case 'credential':
        return { ...baseStyle, background: '#FFCC0020', borderColor: '#FFCC00', color: '#F9FAFB' };
      case 'tool':
        return { ...baseStyle, background: '#34C75920', borderColor: '#34C759', color: '#F9FAFB' };
      case 'resource':
        return { ...baseStyle, background: '#FF3B3020', borderColor: '#FF3B30', color: '#F9FAFB' };
      default:
        return { ...baseStyle, background: '#1A1A1A', borderColor: '#27272A', color: '#F9FAFB' };
    }
  };

  const renderNodeLabel = (node) => {
    const IconComponent = getNodeIcon(node.type);
    return (
      <div className="flex items-center gap-2">
        <IconComponent className="w-4 h-4" />
        <div>
          <div className="font-bold text-xs uppercase text-[#6B7280]">{node.type}</div>
          <div className="text-sm">{node.label}</div>
        </div>
      </div>
    );
  };

  const getNodeIcon = (type) => {
    switch (type) {
      case 'agent': return Bot;
      case 'credential': return Key;
      case 'tool': return Activity;
      case 'resource': return Server;
      default: return AlertTriangle;
    }
  };

  return (
    <div data-testid="graph-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#F9FAFB] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Permission Graph
        </h1>
        <p className="text-[#9CA3AF]">
          Visualize agent permissions: Agent → Credential → Tool → Resource
        </p>
      </div>

      {/* Agent Selector */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex items-center gap-4">
          <Network className="w-5 h-5 text-[#0066FF]" />
          <Select value={selectedAgent} onValueChange={setSelectedAgent}>
            <SelectTrigger className="w-[300px] bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="agent-selector">
              <SelectValue placeholder="Select an agent" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Legend */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#0066FF]/20 border-2 border-[#0066FF]" />
            <span className="text-[#9CA3AF] text-sm">Agent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#FFCC00]/20 border-2 border-[#FFCC00]" />
            <span className="text-[#9CA3AF] text-sm">Credential</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#34C759]/20 border-2 border-[#34C759]" />
            <span className="text-[#9CA3AF] text-sm">Tool</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#FF3B30]/20 border-2 border-[#FF3B30]" />
            <span className="text-[#9CA3AF] text-sm">Resource</span>
          </div>
        </div>
      </div>

      {/* Graph */}
      <div className="enterprise-card overflow-hidden" style={{ height: '600px' }} data-testid="permission-graph">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-[#9CA3AF]">Loading graph...</div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            attributionPosition="bottom-left"
            style={{ background: '#0A0A0A' }}
          >
            <Controls />
            <MiniMap 
              nodeColor={(node) => {
                if (node.id.startsWith('agent')) return '#0066FF';
                if (node.id.startsWith('cred')) return '#FFCC00';
                if (node.id.startsWith('tool')) return '#34C759';
                if (node.id.startsWith('res')) return '#FF3B30';
                return '#27272A';
              }}
              style={{ background: '#121212' }}
            />
            <Background color="#27272A" gap={20} />
          </ReactFlow>
        )}
      </div>
    </div>
  );
};

export default GraphPage;
